// src/tabs/ComptaTab.jsx — onglet Comptabilité (admin only).
//
// Vue mensuelle agrégée — refonte DESIGN (maquette-compta) :
// - Bandeau HERO « Résultat net FBZ » (marine dégradé) + encart équation
//   Commission FBZ + Sous-loc net − Charges = Résultat net ; ménage = flux transitoire
// - Carte « Charges du mois » ÉDITABLE : charges fixes (CRUD) + encarts ambre
//   Dépenses variables (CRUD + import CSV Shine) et Consommables (via Stock)
// - Récap remboursements perso Néïsia/Dimitri (conditionnel)
// - Rentabilité par logement (revenus / charges allouées / résultat net) — compacte
// - Évolution du résultat net (barres 6 mois glissants, mois courant en accent)
// - Suivi ménage (collecté / coût réel / forfait)
//
// `calc` est passé en prop (wrapper App qui referme sur cfg/data/month).
// L'IA d'analyse business est déclenchée via prop `onAnalyse` du parent.
// AUCUN calcul n'a changé vs la version pré-refonte : seule la mise en page /
// l'habillage ont été retravaillés (charte Fraunces + Libre Franklin, DS).

import { useState, useRef } from "react"
import { Sparkles, Mail, Receipt, Plus, Pencil, Trash2, Download, PieChart, BarChart3, Package, AlertTriangle, HeartHandshake, User } from "lucide-react"
import { DS } from "../lib/ui"
import { MONTHS, MONTHS_SHORT, fmtE, uid } from "../lib/format"
// F13 (relecture argent, 2e tour) : données de facturation non lues →
// commission, sous-location nette et résultat net s'affichent « — » (jamais un
// « + 0,00 € » ni un résultat négatif présentés comme vrais) ; « Analyser » est
// désactivé. Les charges (compta, lues à part) restent affichées.
import { siLu, TEXTE_FACTURE_LECTURE_RATEE } from "../lib/argent-lecture"
import { PAYE_PAR_OPTIONS } from "../lib/domain-constants"
import { coutDepotsLog, coutDepotsMoisTousLogs } from "../lib/depots-helpers"
import { computeIncidentComptaFbz } from "../lib/incidents-helpers"
import { calcMenage, calcSousLocForLog } from "../lib/calc"
import { computeResultatNetMois } from "../lib/compta-resultat"
import { getAllLogsFlat } from "../lib/sousloc-helpers"
import { Modal } from "../components/Modal"
import { MonthNav } from "../components/MonthNav"
import { useIsMobile } from "../lib/useIsMobile"
import LoyerLineModal from "../modals/LoyerLineModal"
import DvModal from "../modals/DvModal"
import CsvShineModal from "../modals/CsvShineModal"

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 APPRENTISSAGE DES RÈGLES D'IMPORT BANCAIRE — réparé le 2026-08-05
// ═══════════════════════════════════════════════════════════════════════════
// À l'import d'un relevé Shine, on catégorise chaque ligne (fixe / variable /
// consommable) et on est censé RETENIR la correction faite par l'utilisateur :
//     imported.forEach(r => { if (r.ruleKey) rules[r.ruleKey] = r.cat })
// Or `ruleKey` n'était produit NULLE PART dans le dépôt : la condition était
// toujours fausse, `fbz8-csv-rules` restait vide, et l'apprentissage n'a jamais
// rien appris depuis son écriture. À chaque import, il fallait tout re-corriger
// à la main.
//
// CHOIX ASSUMÉ (conservateur) : la clé retenue est le libellé bancaire
// NORMALISÉ (minuscules, espaces normalisés), et rien de plus. La règle ne se
// déclenchera donc que sur un libellé qui se répète à l'identique — exactement
// le cas des abonnements récurrents (SuperHote, PriceLabs, comptable…), qui
// sont ceux qu'on veut mémoriser. Les paiements par carte, dont le libellé
// contient une date ou une référence unique, ne déclencheront rien : c'est le
// comportement d'aujourd'hui, jamais pire.
//
// ⚠️ NON TRANCHÉ ICI : extraire un « nom de commerçant » d'un libellé bancaire
// (retirer dates, références, préfixes « CARTE 12/07 »…) permettrait de
// mémoriser bien plus de lignes, mais fait courir un risque de sur-application
// — une règle trop large catégoriserait de travers des dépenses sans rapport.
// C'est un arbitrage produit : à décider avec le propriétaire.
const MIN_RULE_KEY_LENGTH = 4

export function buildRuleKey(libelle) {
  const key = String(libelle || '').trim().toLowerCase().replace(/\s+/g, ' ')
  return key.length >= MIN_RULE_KEY_LENGTH ? key : ''
}

// sansMonthNav (lot 5, rangement Argent) : le ‹ mois › du bandeau € Argent
// pilote déjà navM — sous ce chrome, le MonthNav interne serait un doublon
// d'affichage. Prop ADDITIVE, défaut false : l'écran reste tel quel partout
// ailleurs, aucune logique touchée.
export default function ComptaTab({ cfg, data, blocksByMonth, calc, month, y, m, navM, depots, compta, saveCompta, onAnalyse, iaAnalyseLoading, incidents = [], sansMonthNav = false, lectureRatee = false }) {
  // compta + saveCompta hissés dans App.jsx (audit-finding F1) — avant, ce
  // composant tenait son propre state + écriture directe storage, ce qui
  // désynchronisait la KPI 'Marge nette FBZ' de Pilotage > Données après
  // toute modif de charges fixes (App.jsx avait son propre state compta).
  const [cfModal, setCfModal] = useState(null)
  const [dvModal, setDvModal] = useState(null)
  const [confirmDel, setConfirmDel] = useState(null)
  const [csvShine, setCsvShine] = useState([])
  const [csvShineModal, setCsvShineModal] = useState(false)
  const [showAllRentab, setShowAllRentab] = useState(false)
  const isMobile = useIsMobile()
  // Lot 2 : toast d'erreur si l'écriture compta serveur (/api/compta) échoue
  // (calqué sur StockTab). saveCompta (prop App) persiste via l'endpoint et
  // propage l'erreur ; on l'enrobe ici pour la remonter à l'utilisateur.
  const [errToast, setErrToast] = useState(null)
  const errTimerRef = useRef(null)
  const showErrorToast = (message) => {
    if (errTimerRef.current) clearTimeout(errTimerRef.current)
    setErrToast(message)
    errTimerRef.current = setTimeout(() => setErrToast(null), 7000)
  }
  // Renvoie true si la sauvegarde a réussi (pour ne fermer la modale qu'au succès).
  const saveComptaSafe = async (blob) => {
    try { await saveCompta(blob); return true }
    catch (e) { console.error("[ComptaTab] saveCompta", e); showErrorToast(e.message || "Échec de l'enregistrement compta"); return false }
  }
  // Règles d'auto-catégorisation CSV (localStorage) : un JSON corrompu ne doit
  // pas tuer l'import — règles réinitialisées + message.
  const readCsvRules = () => {
    try { return JSON.parse(localStorage.getItem("fbz8-csv-rules") || "{}") }
    catch (e) {
      console.error("[ComptaTab] règles CSV illisibles", e)
      localStorage.removeItem("fbz8-csv-rules")
      showErrorToast("Règles d'import CSV illisibles — elles ont été réinitialisées.")
      return {}
    }
  }
  const monthLabel = `${MONTHS[m - 1]} ${y}`

  if (!compta) return <div style={{textAlign:"center",padding:40,color:DS.navy,fontFamily:DS.fontDisplay}}>Chargement...</div>

  const mk = month
  const chargesFixes = compta.chargesFixes || []
  const totalCF = chargesFixes.reduce((s, c) => s + (c.montant || 0), 0)
  const depensesVar = compta.depensesVar?.[mk] || []
  const totalDV = depensesVar.reduce((s, d) => s + (d.montant || 0), 0)
  // Consommables : refonte 2026-04-29 — coût par logement = somme des dépôts (fbz8-depots-v2)
  // imputés à ce logement sur le mois (coutTotal figé au save). Plus de distinction
  // sacs/prélèvements (ces concepts ont disparu avec la refonte stock).
  const consoReelParLog = getAllLogsFlat(cfg).reduce((acc, l) => {
    acc[l.id] = coutDepotsLog(l.id, mk, depots)
    return acc
  }, {})
  const consoMensuel = Object.values(consoReelParLog).reduce((s, v) => s + v, 0)
  const consoIsAuto = consoMensuel > 0

  // Tous logements (conciergerie + sous-loc)
  const propClients = cfg.props.filter(p => p.id !== "p6")
  const sousLocProps = cfg.props.filter(p => p.id === "p6")
  // ═══════════════════════════════════════════════════════════════════════
  // 🔴 CLÉ DE RÉPARTITION DES CHARGES FIXES — corrigée le 2026-08-05
  // ═══════════════════════════════════════════════════════════════════════
  // On faisait `cfg.props.flatMap(p => p.logs)`, qui donne 9 « logements » :
  //   • il COMPTAIT « Maison Zen » (l8), une coquille de sous-location qui ne
  //     se loue pas — elle n'a ni voyageur ni revenu propre (ADR-197 : elle
  //     n'est même plus éditable) ;
  //   • il OUBLIAIT ses 3 chambres lc1/lc2/lc3, qui elles se louent vraiment
  //     et consomment les mêmes abonnements (SuperHote, PriceLabs, Melo…).
  //
  // Conséquence : le dénominateur valait 9 alors que 11 unités se louent, et
  // la part attribuée à Maison Zen n'était imputée à PERSONNE — 1/9 des
  // charges fixes s'évaporait de la vue « Rentabilité par logement », soit
  // 47,30 € par mois sur les 425,69 € de charges fixes actuelles.
  //
  // On utilise désormais `getAllLogsFlat` — le même helper que les deux autres
  // vues qui répartissent (AdminStatsView et l'onglet Stock), qui expose les
  // FEUILLES réellement louées (8 logements de propriétaires + 3 chambres).
  // Le doublon défensif par id est offert par le helper (anti-bug « x3 »).
  const allLogs = getAllLogsFlat(cfg)
  const nbLogs = allLogs.length || 1

  // Totaux conciergerie
  const totalComm = propClients.reduce((s, p) => s + p.logs.reduce((ss, l) => ss + calc(l.id, l.taux).comm, 0), 0)
  const totalMen = propClients.reduce((s, p) => s + p.logs.reduce((ss, l) => ss + calc(l.id, l.taux).men, 0), 0)

  // Totaux sous-location — PR #169b : blocksByMonth propagé pour inclure résas direct internes
  const sousLocStats = sousLocProps.flatMap(p => p.logs.map(l => calcSousLocForLog(l, mk, data, blocksByMonth)))
  const totalSousLocCA = sousLocStats.reduce((s, x) => s + x.ca, 0)
  const totalSousLocCharges = sousLocStats.reduce((s, x) => s + x.loyer + x.chargesVar, 0)
  const totalSousLocNet = totalSousLocCA - totalSousLocCharges

  // Ménage = flux transitoire par défaut (cas 1). Si un logement a un forfait
  // société de ménage défini (log.menRealCostPerClean > 0), la marge de ce log
  // (collecte - coutReel) est réintégrée ici au titre des revenus bruts (cas 2).
  const totalMargeMenage = propClients.reduce((s, p) => s + p.logs.reduce((ss, l) => {
    const mm = calcMenage(l, mk, data)
    return ss + (mm.margeActive ? mm.marge : 0)
  }, 0), 0)
  const totalRevenusBruts = totalComm + totalSousLocNet + totalMargeMenage
  // Chantier ⑥ — charge FBZ des incidents « Achat FULLBNBZEN » du mois (lecture à
  // la volée : jamais écrit dans depensesVar), atténuée par l'AirCover perçu par
  // FBZ en sous-loc. Seul « Achat FULLBNBZEN » est une charge ; « Refacturé
  // propriétaire » (porté par la facture) et « Remboursé AirCover » sont neutres.
  const incidentFbz = computeIncidentComptaFbz(incidents, mk)
  const incidentFbzNet = incidentFbz.net
  const totalCharges = totalCF + totalDV + consoMensuel + incidentFbzNet
  // Formule UNIQUE, partagée avec la barre du graphe annuel (cf. months12
  // ci-dessous) : les deux affichages ne peuvent plus diverger.
  const resultatNet = computeResultatNetMois({
    commissions: totalComm,
    margeMenage: totalMargeMenage,
    sousLocNet: totalSousLocNet,
    chargesFixes: totalCF,
    depensesVar: totalDV,
    consommables: consoMensuel,
    incidentsNet: incidentFbzNet,
  })

  // Données annuelles pour graphique
  const months12 = Array.from({ length: 12 }, (_, i) => {
    const mk2 = `${y}-${String(i+1).padStart(2,'0')}`
    const comm2 = propClients.reduce((s, p) => s + p.logs.reduce((ss, l) => ss + calc(l.id, l.taux, mk2).comm, 0), 0)
    const marge2 = propClients.reduce((s, p) => s + p.logs.reduce((ss, l) => {
      const mm = calcMenage(l, mk2, data)
      return ss + (mm.margeActive ? mm.marge : 0)
    }, 0), 0)
    // Consommables RÉELS du mois mk2 = somme des dépôts (fbz8-depots-v2) imputés à
    // chaque logement sur mk2 (ADR-210), même source que `consoMensuel` (mois courant)
    // mais paramétrée par mk2 pour fournir le montant PAR MOIS attendu par le graphe.
    // Remplace l'ancien champ MORT compta.consommables[mk2] (jamais écrit → 0).
    // Source DISJOINTE de dv2 (compta.depensesVar) → aucun double-comptage, cohérent
    // avec le calcul du mois courant (totalCF + totalDV + consoMensuel, l.110-111).
    const conso2 = coutDepotsMoisTousLogs(allLogs, mk2, depots)
    const dv2 = (compta.depensesVar?.[mk2] || []).reduce((s, d) => s + (d.montant || 0), 0)
    // ═══════════════════════════════════════════════════════════════════
    // 🔴 LE GRAPHE NE DISAIT PAS LA MÊME CHOSE QUE LE RÉSULTAT NET
    // ═══════════════════════════════════════════════════════════════════
    // Corrigé le 2026-08-05. La barre du mois valait `comm2 + marge2 -
    // charges` : elle OUBLIAIT deux composantes que le grand chiffre
    // « Résultat net » (plus haut dans le même écran) compte bien —
    //   1. le bénéfice de SOUS-LOCATION (Maison Zen : CA − loyer − charges),
    //   2. la charge nette des INCIDENTS « Achat FULLBNBZEN ».
    // Sur les mois où la sous-location tourne, la barre pouvait donc être
    // très en dessous — ou au-dessus — du résultat réellement annoncé, dans
    // le même écran, pour le même mois. Le graphe suit maintenant EXACTEMENT
    // la formule du bandeau (cf. `resultatNet` ci-dessus).
    const sousLocNet2 = sousLocProps.reduce(
      (s, p) => s + p.logs.reduce(
        (ss, l) => {
          const sl = calcSousLocForLog(l, mk2, data, blocksByMonth)
          return ss + (sl.ca - sl.loyer - sl.chargesVar)
        }, 0), 0)
    const incidentNet2 = computeIncidentComptaFbz(incidents, mk2).net
    // MÊME formule que le bandeau « Résultat net » — un seul exemplaire.
    const net2 = computeResultatNetMois({
      commissions: comm2,
      margeMenage: marge2,
      sousLocNet: sousLocNet2,
      chargesFixes: totalCF,
      depensesVar: dv2,
      consommables: conso2,
      incidentsNet: incidentNet2,
    })
    return { label: MONTHS_SHORT[i], net: net2, comm: comm2, actif: comm2 > 0 || sousLocNet2 !== 0 }
  })

  // Rentabilité par logement
  const rentabiliteParLog = propClients.flatMap(prop => prop.logs.map(log => {
    const c = calc(log.id, log.taux)
    const totalInvesti = (log.investissement || []).reduce((s, i) => s + (i.montant || 0), 0)
    // Commissions cumulées tous mois — PR #169b (H-5) : remplace inline buggué
    // (ne gérait pas commissionMode dégressif ni pricing_blocks direct internes)
    // par appel wrapper `calc` enrichi qui couvre les 2 cas via calcPure + mergeCalc.
    // Union des monthkeys data KV + blocksByMonth pour ne pas rater un mois
    // qui aurait UNIQUEMENT des résas direct internes (pas de KV).
    const monthsKv = Object.keys(data || {})
    const monthsBlocks = blocksByMonth ? Array.from(blocksByMonth.keys()) : []
    const allMonths = Array.from(new Set([...monthsKv, ...monthsBlocks])).sort()
    const commCumulee = allMonths.reduce((s, mk2) => s + calc(log.id, log.taux, mk2).comm, 0)
    // Quote-part charges fixes ce mois
    const cfParLog = totalCF / nbLogs
    const consoParLog = consoReelParLog[log.id] || 0
    // Dépenses variables taguées ce logement
    const dvParLog = depensesVar.filter(d => d.logId === log.id).reduce((s, d) => s + (d.montant || 0), 0)
    const dvGlobal = depensesVar.filter(d => !d.logId).reduce((s, d) => s + (d.montant || 0), 0) / nbLogs
    const totalChargesLog = cfParLog + consoParLog + dvParLog + dvGlobal
    const netLog = c.comm - totalChargesLog
    const rentable = totalInvesti === 0 || commCumulee >= totalInvesti
    const pct = totalInvesti > 0 ? Math.min(100, Math.round(commCumulee / totalInvesti * 100)) : 100
    return { prop, log, c, totalInvesti, commCumulee, rentable, pct, cfParLog, consoParLog, dvParLog, dvGlobal, totalChargesLog, netLog }
  }))

  const saveCF = async (line) => {
    const idx = chargesFixes.findIndex(c => c.id === line.id)
    const newCF = idx >= 0 ? chargesFixes.map((c, i) => i === idx ? line : c) : [...chargesFixes, line]
    if (await saveComptaSafe({ ...compta, chargesFixes: newCF })) setCfModal(null)
  }
  const deleteCF = async (id) => { if (await saveComptaSafe({ ...compta, chargesFixes: chargesFixes.filter(c => c.id !== id) })) setConfirmDel(null) }
  const saveDV = async (line) => {
    const idx = depensesVar.findIndex(d => d.id === line.id)
    const newDV = idx >= 0 ? depensesVar.map((d, i) => i === idx ? line : d) : [...depensesVar, line]
    if (await saveComptaSafe({ ...compta, depensesVar: { ...compta.depensesVar, [mk]: newDV } })) setDvModal(null)
  }
  const deleteDV = async (id) => { if (await saveComptaSafe({ ...compta, depensesVar: { ...compta.depensesVar, [mk]: depensesVar.filter(d => d.id !== id) } })) setConfirmDel(null) }

  // Import CSV Shine — parse + auto-catégorisation (inchangé). Extrait en fonction
  // pour être branché sur le bouton « CSV Shine » de l'encart Dépenses variables.
  const handleCsvFile = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = evt => {
      const lines = evt.target.result.split(/\r?\n/).filter(l => l.trim())
      if (!lines.length) {
        showErrorToast("CSV vide ou illisible — rien à importer.")
        return
      }
      const sep = ";"
      const headers = lines[0].split(sep).map(h => h.replace(/"/g, '').trim())
      const rows = lines.slice(1).map(line => {
        const cells = line.split(sep).map(c => c.replace(/"/g, '').trim())
        const row = {}
        headers.forEach((h, i) => row[h] = cells[i] || "")
        return row
      }).filter(r => r["Libellé"] || r["Nom de la contrepartie"])
      // Convertir les débits en dépenses
      const parsed = rows.filter(r => {
        const debit = parseFloat((r["Débit"] || "0").replace(",", "."))
        return debit > 0
      }).map(r => {
        const debit = parseFloat((r["Débit"] || "0").replace(",", "."))
        const libelle = r["Libellé"] || r["Nom de la contrepartie"] || ""
        const dateRaw = r["Date de la valeur"] || r["Date d'opération"] || ""
        // Convertir date DD/MM/YYYY → YYYY-MM-DD
        const dateParts = dateRaw.split("/")
        const dateIso = dateParts.length === 3 ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}` : dateRaw
        // Auto-catégorisation via règles
        const rules = readCsvRules()
        const libLow = libelle.toLowerCase()
        let cat = "variable"
        for (const [kw, c] of Object.entries(rules)) {
          // Garde : un mot-clé vide ou trop court capturerait TOUTES les lignes
          // et catégoriserait de travers tout un relevé.
          if (!kw || kw.trim().length < MIN_RULE_KEY_LENGTH) continue
          if (libLow.includes(kw.toLowerCase())) { cat = c; break }
        }
        // Règles par défaut
        if (cat === "variable") {
          const defaultRules = [
            ["metro", "consommable"], ["bm pro", "consommable"], ["action", "consommable"],
            ["carrefour", "consommable"], ["leclerc", "consommable"], ["lidl", "consommable"],
            ["superhote", "fixe"], ["pricelabs", "fixe"], ["free", "fixe"], ["comptable", "fixe"],
            ["avocats", "fixe"], ["assurance", "fixe"], ["edf", "fixe"], ["engie", "fixe"],
          ]
          for (const [kw, c] of defaultRules) {
            if (libLow.includes(kw)) { cat = c; break }
          }
        }
        // `ruleKey` : clé d'apprentissage de la catégorie (cf. buildRuleKey).
        // Elle n'était produite NULLE PART → l'apprentissage était mort.
        return { id: uid(), libelle, montant: debit, date: dateIso, cat, payePar: "shine", done: false, ruleKey: buildRuleKey(libelle) }
      })
      setCsvShine(parsed)
      setCsvShineModal(true)
    }
    reader.readAsText(file, "UTF-8")
    e.target.value = ""
  }

  // ── Lignes de rentabilité (conciergerie + sous-loc) pour la liste compacte ──
  const rentabRows = [
    ...rentabiliteParLog.map(({ log, c, totalChargesLog, netLog }) => ({
      id: log.id, nom: log.nom,
      // F13 : lecture ratée → ni « rev. 0,00 € » ni net négatif présentés comme vrais.
      sub: lectureRatee ? "—" : `rev. ${fmtE(c.comm)} − charges ${fmtE(totalChargesLog)}`,
      net: siLu(lectureRatee, netLog),
    })),
    ...sousLocProps.flatMap(p => p.logs.map(log => {
      const s = calcSousLocForLog(log, mk, data, blocksByMonth)
      return { id: log.id, nom: log.nom, sub: lectureRatee ? "—" : `CA ${fmtE(s.ca)} − charges ${fmtE(s.loyer + s.chargesVar)}`, net: siLu(lectureRatee, s.net) }
    })),
  ]
  const visibleRentab = showAllRentab ? rentabRows : rentabRows.slice(0, 4)

  // ── Évolution : fenêtre glissante de 6 mois se terminant au mois courant ──
  const evoStart = Math.min(Math.max(0, m - 6), 6)
  const evoWindow = months12.slice(evoStart, evoStart + 6)
  const evoMax = Math.max(...evoWindow.map(x => Math.abs(x.net)), 1)

  // ── Suivi ménage : totaux du mois (par logement conciergerie) ──
  const menageRows = propClients.flatMap(p => p.logs.map(l => calcMenage(l, mk, data)))
  const menageActive = menageRows.some(r => r.margeActive)
  const menageCollecte = menageRows.reduce((s, r) => s + r.collecte, 0)
  const menageCoutReel = menageRows.reduce((s, r) => s + (r.coutReel || 0), 0)
  const menageMarge = menageRows.reduce((s, r) => s + (r.marge || 0), 0)
  const menageInterv = menageRows.reduce((s, r) => s + r.nbInterventions, 0)

  const AMBRE_BG = "#FBF3EC"
  const AMBRE_TXT = DS.warningText // #854F0B
  const VERT = DS.successText      // #0F6E56
  const cardStyle = { background: DS.white, border: `0.5px solid ${DS.border}`, borderRadius: 14, padding: "20px 22px", marginBottom: 16 }
  const spiStyle = (bg, color) => ({ width: 32, height: 32, borderRadius: 9, background: bg, color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 })
  const stitle = { fontFamily: DS.fontDisplay, fontWeight: 600, fontSize: 17, color: DS.marineDark }
  // F13 : une valeur inconnue (lecture ratée, NaN) n'est ni verte ni rouge.
  const netColor = (n) => (!Number.isFinite(n) ? DS.textMuted : n >= 0 ? VERT : DS.dangerText)

  return (
    <div style={{fontFamily:DS.fontBody,color:DS.textPrimary}}>
      {errToast && (
        <div role="alert"
          style={{position:"fixed",bottom:24,right:24,zIndex:1003,background:DS.danger,color:"#fff",padding:"10px 14px",borderRadius:DS.radiusControl,boxShadow:"0 8px 24px rgba(0,0,0,.25)",display:"flex",alignItems:"center",gap:12,fontFamily:DS.fontBody,fontSize:12,fontWeight:600,maxWidth:340}}>
          <span style={{display:"flex",alignItems:"center",gap:7}}><AlertTriangle size={15} strokeWidth={2} style={{flexShrink:0}}/> {errToast}</span>
          <button onClick={() => setErrToast(null)}
            style={{background:"none",color:"rgba(255,255,255,.6)",border:"none",fontSize:16,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
      )}
      {confirmDel?.type === "cf" && <Modal title="Supprimer cette charge ?" msg="Action irr&#233;versible." onOk={() => deleteCF(confirmDel.id)} onCancel={() => setConfirmDel(null)}/>}
      {confirmDel?.type === "dv" && <Modal title="Supprimer cette d&#233;pense ?" msg="Action irr&#233;versible." onOk={() => deleteDV(confirmDel.id)} onCancel={() => setConfirmDel(null)}/>}
      {cfModal && <LoyerLineModal line={cfModal === "new" ? null : cfModal} onSave={saveCF} onClose={() => setCfModal(null)}/>}
      {dvModal && <DvModal dv={dvModal === "new" ? null : dvModal} cfg={cfg} onSave={saveDV} onClose={() => setDvModal(null)}/>}
      {csvShineModal && <CsvShineModal rows={csvShine} cfg={cfg} mk={mk} onImport={(imported) => {
        // Sauvegarder les règles apprises
        const rules = readCsvRules()
        imported.forEach(r => { if (r.ruleKey) rules[r.ruleKey] = r.cat })
        localStorage.setItem("fbz8-csv-rules", JSON.stringify(rules))
        // Ajouter aux dépenses variables
        const newDV = [...depensesVar]
        imported.filter(r => r.cat === 'variable' || r.cat === 'consommable').forEach(r => {
          if (!newDV.find(d => d.id === r.id)) newDV.push({ id: r.id, label: r.libelle, montant: r.montant, logId: r.logId || "", payePar: "shine", date: r.date })
        })
        // Ajouter aux charges fixes si cat=fixe
        const newCF = [...chargesFixes]
        imported.filter(r => r.cat === 'fixe').forEach(r => {
          if (!newCF.find(c => (c.label || "").toLowerCase() === r.libelle.toLowerCase())) {
            newCF.push({ id: uid(), label: r.libelle, montant: r.montant })
          }
        })
        saveComptaSafe({ ...compta, depensesVar: { ...compta.depensesVar, [mk]: newDV }, chargesFixes: newCF })
          .then(ok => { if (ok) setCsvShineModal(false) })
      }} onClose={() => setCsvShineModal(false)}/>}

      {/* ── Barre : sélecteur de mois + Analyser ce mois avec Claude ── */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:14,flexWrap:"wrap",marginBottom:2}}>
        {sansMonthNav ? <span/> : <MonthNav y={y} m={m} label={monthLabel} nav={navM}/>}
        <div style={{display:"flex",flexDirection:"column",alignItems:isMobile?"flex-start":"flex-end",gap:4,marginBottom:14}}>
          <button onClick={onAnalyse} disabled={iaAnalyseLoading||lectureRatee} className={(iaAnalyseLoading||lectureRatee)?undefined:"fbz-press"}
            style={{background:DS.white,border:`0.5px solid ${DS.borderHover}`,color:DS.marine,borderRadius:9,padding:"9px 14px",fontWeight:600,fontSize:12.5,cursor:lectureRatee?"not-allowed":iaAnalyseLoading?"wait":"pointer",fontFamily:DS.fontBody,display:"inline-flex",alignItems:"center",gap:7,opacity:(iaAnalyseLoading||lectureRatee)?.65:1}}>
            <Sparkles size={15} strokeWidth={2} color={DS.corail}/> {lectureRatee ? TEXTE_FACTURE_LECTURE_RATEE : iaAnalyseLoading ? "Analyse en cours…" : "Analyser ce mois avec Claude"}
          </button>
          <div style={{fontSize:10.5,color:DS.textMuted,display:"flex",alignItems:"center",gap:5}}>
            <Mail size={11} strokeWidth={2}/> Rapport auto chaque matin à 7h · ~0,01 €/analyse
          </div>
        </div>
      </div>

      {/* ── HERO : Résultat net FBZ ── */}
      <div style={{position:"relative",overflow:"hidden",background:`linear-gradient(135deg, ${DS.marine} 0%, ${DS.marine} 55%, ${DS.marineDark} 100%)`,borderRadius:16,padding:"24px 26px",marginBottom:16,color:"#fff",display:"flex",alignItems:"center",justifyContent:"space-between",gap:30,flexWrap:"wrap"}}>
        <div aria-hidden="true" style={{position:"absolute",right:-50,top:-60,width:200,height:200,borderRadius:"50%",background:"rgba(135,184,230,.10)",pointerEvents:"none"}}/>
        <div style={{position:"relative",minWidth:200}}>
          <div style={{fontSize:12,letterSpacing:2,color:"#9fc2e6",fontWeight:600,marginBottom:12,textTransform:"uppercase"}}>R&#233;sultat net FBZ · {monthLabel}</div>
          <div style={{fontFamily:DS.fontDisplay,fontWeight:700,fontSize:46,lineHeight:1,color:"#fff"}}>{fmtE(siLu(lectureRatee,resultatNet))}</div>
          <div style={{fontSize:12.5,color:"#9fc2e6",marginTop:12}}>M&#233;nage collect&#233; {fmtE(siLu(lectureRatee,totalMen))} = flux transitoire (n&#39;impacte pas le net)</div>
        </div>
        <div style={{position:"relative",minWidth:238,flex:isMobile?"1 1 100%":"0 0 auto",background:"rgba(255,255,255,.06)",borderRadius:12,padding:"14px 18px"}}>
          {[
            { l: "Commission FBZ", v: lectureRatee ? fmtE(NaN) : `+ ${fmtE(totalComm)}`, c: "#fff" },
            { l: "Sous-loc net", v: lectureRatee ? fmtE(NaN) : `${totalSousLocNet >= 0 ? "+ " : "− "}${fmtE(Math.abs(totalSousLocNet))}`, c: "#fff" },
            { l: "Charges", v: `− ${fmtE(totalCharges)}`, c: "#f5c4b3" },
          ].map((row, i) => (
            <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:13.5,padding:"5px 0"}}>
              <span style={{color:"#bcd5ee"}}>{row.l}</span>
              <span style={{color:row.c,fontWeight:600}}>{row.v}</span>
            </div>
          ))}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",borderTop:"0.5px solid rgba(255,255,255,.15)",marginTop:4,paddingTop:9}}>
            <span style={{color:"#fff",fontWeight:600,fontSize:13.5}}>R&#233;sultat net</span>
            <span style={{fontFamily:DS.fontDisplay,color:"#fff",fontWeight:700,fontSize:17}}>{fmtE(siLu(lectureRatee,resultatNet))}</span>
          </div>
        </div>
      </div>

      {/* ── Charges du mois (éditable) ── */}
      <div style={cardStyle}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:6,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:11}}>
            <span style={spiStyle(DS.corailSoft, DS.corailText)}><Receipt size={17} strokeWidth={2}/></span>
            <div>
              <div style={stitle}>Charges du mois</div>
              <div style={{fontSize:13,color:DS.textSecondary}}>Total <b style={{color:DS.marineDark}}>{fmtE(totalCharges)}</b> · {fmtE(totalCF/nbLogs)}/logement</div>
            </div>
          </div>
          <button onClick={() => setCfModal("new")} className="fbz-press"
            style={{background:DS.marine,color:"#fff",border:"none",borderRadius:9,padding:"9px 14px",fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody,display:"inline-flex",alignItems:"center",gap:6}}>
            <Plus size={14} strokeWidth={2.4}/> Ajouter
          </button>
        </div>

        <div style={{fontSize:11,letterSpacing:.5,fontWeight:600,color:DS.textMuted,marginTop:14}}>CHARGES FIXES</div>
        {chargesFixes.length === 0 && <div style={{fontSize:13,color:"#c4c7cf",padding:"11px 0"}}>Aucune charge fixe</div>}
        {chargesFixes.map(c => (
          <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 0",borderTop:`0.5px solid #f1f1f3`}}>
            <span style={{fontSize:14,color:DS.textPrimary,flex:1,minWidth:0}}>{c.label}</span>
            <span style={{fontSize:12,color:DS.textMuted}}>{fmtE(c.montant/nbLogs)}/log</span>
            <span style={{fontFamily:DS.fontDisplay,fontWeight:600,color:DS.marineDark,minWidth:80,textAlign:"right"}}>{fmtE(c.montant)}</span>
            <button onClick={() => setCfModal(c)} title="Éditer" style={{background:"none",border:"none",cursor:"pointer",padding:2,display:"inline-flex",alignItems:"center"}}><Pencil size={15} strokeWidth={1.9} color={DS.textMuted}/></button>
            <button onClick={() => setConfirmDel({ type: "cf", id: c.id })} title="Supprimer" style={{background:"none",border:"none",cursor:"pointer",padding:2,display:"inline-flex",alignItems:"center"}}><Trash2 size={15} strokeWidth={1.9} color="#d9a3a3"/></button>
          </div>
        ))}

        {/* Encarts ambre : dépenses variables + consommables */}
        <div className="fbz-grid-collapse" style={{marginTop:18}}>
          <div style={{background:AMBRE_BG,borderRadius:11,padding:"14px 16px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
              <span style={{fontSize:11,letterSpacing:.7,fontWeight:600,color:AMBRE_TXT}}>DÉPENSES VARIABLES</span>
              <div style={{display:"flex",gap:6}}>
                <label className="fbz-press" style={{background:DS.white,border:`0.5px solid ${DS.borderHover}`,color:DS.marine,borderRadius:9,padding:"5px 10px",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody,display:"inline-flex",alignItems:"center",gap:5}}>
                  <Download size={13} strokeWidth={2}/> CSV Shine
                  <input type="file" accept=".csv" style={{display:"none"}} onChange={handleCsvFile}/>
                </label>
                <button onClick={() => setDvModal("new")} className="fbz-press"
                  style={{background:DS.white,border:`0.5px solid ${DS.borderHover}`,color:DS.marine,borderRadius:9,padding:"5px 10px",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody,display:"inline-flex",alignItems:"center",gap:4}}>
                  <Plus size={13} strokeWidth={2.4}/> Manuel
                </button>
              </div>
            </div>
            <div style={{fontSize:13,color:DS.textSecondary,marginTop:10}}>
              {depensesVar.length === 0 ? <>Aucune d&#233;pense ce mois · <b style={{color:DS.marineDark}}>{fmtE(0)}</b></>
                : <>{depensesVar.length} d&#233;pense{depensesVar.length>1?"s":""} · <b style={{color:DS.marineDark}}>{fmtE(totalDV)}</b></>}
            </div>
          </div>
          <div style={{background:AMBRE_BG,borderRadius:11,padding:"14px 16px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
              <span style={{fontSize:11,letterSpacing:.7,fontWeight:600,color:AMBRE_TXT}}>CONSOMMABLES</span>
              <span style={{fontSize:11,color:AMBRE_TXT,display:"inline-flex",alignItems:"center",gap:4}}><Package size={12} strokeWidth={2}/> via Stock</span>
            </div>
            <div style={{fontSize:13,color:DS.textSecondary,marginTop:10}}>
              {consoIsAuto
                ? <><b style={{color:DS.marineDark}}>{fmtE(consoMensuel)}</b> · calcul&#233; depuis les d&#233;p&#244;ts r&#233;els</>
                : <><b style={{color:DS.marineDark}}>{fmtE(0)}</b> · saisis tes d&#233;p&#244;ts dans Stock</>}
            </div>
          </div>
          {/* Chantier ⑥ — INCIDENTS (Achat FULLBNBZEN) : charge lue à la volée
              depuis les incidents (jamais dupliquée en base). Affiché seulement si
              une charge existe ce mois. */}
          {(incidentFbz.achat > 0 || incidentFbz.aircoverFbz > 0) && (
            <div style={{background:AMBRE_BG,borderRadius:11,padding:"14px 16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <span style={{fontSize:11,letterSpacing:.7,fontWeight:600,color:AMBRE_TXT}}>INCIDENTS (ACHAT FBZ)</span>
                <span style={{fontSize:11,color:AMBRE_TXT,display:"inline-flex",alignItems:"center",gap:4}}><AlertTriangle size={12} strokeWidth={2}/> via Incidents</span>
              </div>
              <div style={{fontSize:13,color:DS.textSecondary,marginTop:10}}>
                <b style={{color:DS.marineDark}}>{fmtE(incidentFbzNet)}</b>
                {incidentFbz.aircoverFbz > 0
                  ? <> · {fmtE(incidentFbz.achat)} achats &#8722; {fmtE(incidentFbz.aircoverFbz)} AirCover FBZ</>
                  : <> · rachats absorb&#233;s par FULLBNBZEN</>}
              </div>
            </div>
          )}
        </div>

        {/* Détail éditable des dépenses variables du mois (CRUD conservé) */}
        {depensesVar.length > 0 && (
          <div style={{marginTop:16}}>
            {depensesVar.map(d => {
              const ppOpt = PAYE_PAR_OPTIONS.find(o => o.value === (d.payePar || "shine"))
              const logNom = d.logId ? (allLogs.find(l => l.id === d.logId)?.nom || null) : null
              return (
                <div key={d.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderTop:`0.5px solid #f1f1f3`}}>
                  <div style={{flex:1,minWidth:0}}>
                    <span style={{fontSize:14,color:DS.textPrimary}}>{d.label}</span>
                    {logNom
                      ? <span style={{fontSize:10.5,color:DS.marine,background:DS.cielSoft,borderRadius:4,padding:"1px 6px",marginLeft:6}}>{logNom}</span>
                      : <span style={{fontSize:10.5,color:DS.textMuted,marginLeft:6}}>global</span>}
                    {ppOpt && ppOpt.value !== 'shine' && (
                      <span style={{fontSize:9.5,background:ppOpt.color+"20",color:ppOpt.color,borderRadius:4,padding:"1px 6px",marginLeft:4,fontWeight:700}}>{ppOpt.label}</span>
                    )}
                  </div>
                  <span style={{fontFamily:DS.fontDisplay,fontWeight:600,color:DS.charge,minWidth:80,textAlign:"right"}}>{fmtE(d.montant)}</span>
                  <button onClick={() => setDvModal(d)} title="Éditer" style={{background:"none",border:"none",cursor:"pointer",padding:2,display:"inline-flex",alignItems:"center"}}><Pencil size={15} strokeWidth={1.9} color={DS.textMuted}/></button>
                  <button onClick={() => setConfirmDel({ type: "dv", id: d.id })} title="Supprimer" style={{background:"none",border:"none",cursor:"pointer",padding:2,display:"inline-flex",alignItems:"center"}}><Trash2 size={15} strokeWidth={1.9} color="#d9a3a3"/></button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Récap remboursements Néïsia / Dimitri (conditionnel) ── */}
      {(() => {
        const rembNeisia = depensesVar.filter(d => d.payePar === 'neisia').reduce((s, d) => s + (d.montant || 0), 0)
        const rembDimitri = depensesVar.filter(d => d.payePar === 'dimitri').reduce((s, d) => s + (d.montant || 0), 0)
        if (rembNeisia === 0 && rembDimitri === 0) return null
        return (
          <div style={{background:"#f5f0fa",borderRadius:14,padding:"18px 20px",marginBottom:16,border:"0.5px solid #d8c8f0"}}>
            <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:10}}>
              <span style={spiStyle("#EEEDFE","#6b3fa0")}><HeartHandshake size={17} strokeWidth={2}/></span>
              <div style={{...stitle,color:"#6b3fa0"}}>Remboursements à effectuer</div>
            </div>
            {rembNeisia > 0 && (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderTop:"0.5px solid #e8dcf8"}}>
                <span style={{fontSize:14,color:DS.textPrimary,fontWeight:500,display:"flex",alignItems:"center",gap:6}}><User size={14} strokeWidth={2}/> Néïsia perso</span>
                <span style={{fontFamily:DS.fontDisplay,fontSize:16,fontWeight:700,color:"#7b5ea7"}}>{fmtE(rembNeisia)}</span>
              </div>
            )}
            {rembDimitri > 0 && (
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderTop:"0.5px solid #e8dcf8"}}>
                <span style={{fontSize:14,color:DS.textPrimary,fontWeight:500,display:"flex",alignItems:"center",gap:6}}><User size={14} strokeWidth={2}/> Dimitri perso</span>
                <span style={{fontFamily:DS.fontDisplay,fontSize:16,fontWeight:700,color:"#2d7a5e"}}>{fmtE(rembDimitri)}</span>
              </div>
            )}
            <div style={{fontSize:11,color:DS.textMuted,marginTop:8}}>Ces montants ont été avancés sur compte personnel — à rembourser depuis Shine</div>
          </div>
        )
      })()}

      {/* ── Grille : Rentabilité | (Évolution + Suivi ménage) ── */}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1.1fr 1fr",gap:16,alignItems:"start"}}>

        {/* Rentabilité par logement */}
        <div style={{...cardStyle,marginBottom:0}}>
          <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:3}}>
            <span style={spiStyle(DS.successBg, VERT)}><PieChart size={17} strokeWidth={2}/></span>
            <div>
              <div style={stitle}>Rentabilité par logement</div>
              <div style={{fontSize:12.5,color:DS.textSecondary}}>résultat net après charges allouées</div>
            </div>
          </div>
          {rentabRows.length === 0 && <div style={{fontSize:13,color:"#c4c7cf",textAlign:"center",padding:"18px 0"}}>Aucune donn&#233;e — saisir des r&#233;servations d&#39;abord</div>}
          {visibleRentab.map(r => (
            <div key={r.id} style={{display:"flex",alignItems:"center",gap:14,padding:"13px 0",borderTop:`0.5px solid #f1f1f3`}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontFamily:DS.fontDisplay,fontWeight:600,fontSize:14,color:DS.marineDark}}>{r.nom}</div>
                <div style={{fontSize:12,color:DS.textMuted}}>{r.sub}</div>
              </div>
              <span style={{fontFamily:DS.fontDisplay,fontWeight:700,fontSize:17,color:netColor(r.net)}}>{fmtE(r.net)}</span>
            </div>
          ))}
          {rentabRows.length > 4 && (
            <div style={{textAlign:"center",marginTop:12}}>
              <button onClick={() => setShowAllRentab(v => !v)}
                style={{background:"none",border:"none",fontSize:12.5,color:DS.marine,fontWeight:600,cursor:"pointer",fontFamily:DS.fontBody}}>
                {showAllRentab ? "Réduire ›" : `Voir les ${rentabRows.length} logements ›`}
              </button>
            </div>
          )}
        </div>

        {/* Colonne droite : évolution + suivi ménage */}
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {/* Évolution du résultat net */}
          <div style={{...cardStyle,marginBottom:0}}>
            <div style={{display:"flex",alignItems:"center",gap:11}}>
              <span style={spiStyle(DS.catIndigoBg, DS.catIndigoIco)}><BarChart3 size={17} strokeWidth={2}/></span>
              <div style={stitle}>Évolution du résultat net</div>
            </div>
            {/* F13 : lecture ratée → pas de barres calculées sur un vide (une
                barre rouge « négative » se lirait comme vraie). */}
            {lectureRatee ? (
              <div data-testid="compta-evolution-inconnue" style={{display:"flex",alignItems:"center",justifyContent:"center",height:90,marginTop:16,fontFamily:DS.fontDisplay,fontWeight:700,fontSize:22,color:DS.textMuted}}>—</div>
            ) : (
            <div style={{display:"flex",alignItems:"flex-end",gap:8,height:90,marginTop:16}}>
              {evoWindow.map((mm, i) => {
                const isCurrent = (evoStart + i + 1) === m
                const h = mm.net === 0 ? 3 : Math.max(4, Math.round(Math.abs(mm.net) / evoMax * 78))
                const color = mm.net < 0 ? DS.danger : isCurrent ? DS.catIndigoIco : "#c7bdf0"
                return (
                  <div key={i} style={{flex:1,textAlign:"center"}}>
                    <div title={fmtE(mm.net)} style={{height:h,background:color,borderRadius:"3px 3px 0 0"}}/>
                    <div style={{fontSize:12,color:isCurrent?DS.marineDark:DS.textMuted,fontWeight:isCurrent?600:400,marginTop:5}}>{mm.label}</div>
                  </div>
                )
              })}
            </div>
            )}
          </div>

          {/* Suivi ménage */}
          <div style={{...cardStyle,marginBottom:0}}>
            <div style={{display:"flex",alignItems:"center",gap:11}}>
              <span style={spiStyle(DS.cielSoft, DS.marine)}><Sparkles size={17} strokeWidth={2}/></span>
              <div style={stitle}>Suivi ménage</div>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:14,fontSize:13.5}}>
              {/* F13 : collecte, interventions, coût réel et marge viennent tous
                  des réservations du blob de facturation (calcMenage) — « — »
                  pendant une lecture ratée. Le forfait (configuration) ne fait
                  que décider si la marge se calcule. */}
              <span style={{color:DS.textSecondary}}>Collect&#233; ({lectureRatee ? "—" : <>{menageInterv} intervention{menageInterv>1?"s":""}</>})</span>
              <span style={{fontWeight:600,color:"#B5820E"}}>{fmtE(siLu(lectureRatee,menageCollecte))}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10,fontSize:13.5}}>
              <span style={{color:DS.textSecondary}}>Co&#251;t r&#233;el</span>
              <span style={{color:menageActive?DS.charge:DS.textMuted,fontWeight:menageActive?600:400}}>{menageActive ? fmtE(siLu(lectureRatee,menageCoutReel)) : "— forfait non d&#233;fini"}</span>
            </div>
            {menageActive && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10,fontSize:13.5}}>
                <span style={{color:DS.textSecondary}}>Marge</span>
                <span style={{fontWeight:700,color:netColor(siLu(lectureRatee,menageMarge))}}>{fmtE(siLu(lectureRatee,menageMarge))}</span>
              </div>
            )}
            <div style={{fontSize:12,color:DS.textMuted,marginTop:12,paddingTop:12,borderTop:"0.5px solid #f1f1f3",lineHeight:1.5}}>
              {menageActive
                ? "Marge = ménage collecté auprès des voyageurs − forfait payé à la société de ménage."
                : "Renseigne un forfait société de ménage dans Params → Général pour activer le calcul de marge."}
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
